import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { UserRole } from '@repo/shared/types';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert';
import { Button } from '@repo/ui/button';
import { formatDate } from '@repo/ui/lib/formatters';
import { Link } from 'react-router';
import { TokenDeleteDialog } from '@/components/dialogs/token-delete-dialog';
import { ApiTokenForm } from '@/components/forms/api-token-form';
import { SettingsHeader } from '@/components/settings-header';
import { trpc } from '@/providers/trpc';
import { useOrganizationStore } from '@/stores/organization-store';

export const ApiTokensPage = () => {
  useLocale();

  const { data: tokens } = trpc.apiToken.listApiTokens.useQuery({});
  const { currentOrganization } = useOrganizationStore();

  return (
    <div className="raven-page raven-settings">
      <SettingsHeader
        subtitle={
          <>
            {t('auth.onThisPageYouCanCreateAndManageApi')}{' '}
            <Link className="text-primary underline" rel="noopener" target="_blank" to="/docs">
              {t('auth.documentation')}{' '}
            </Link>{' '}
            {t('auth.forMoreInformation')}{' '}
          </>
        }
        title={t('workspace.apiTokens')}
      />

      {currentOrganization?.currentOrganizationRole !== UserRole.ORG_OWNER ? (
        <Alert
          className="flex flex-col items-center justify-between gap-4 p-6 md:flex-row"
          variant="destructive"
        >
          <div>
            <AlertTitle>{t('auth.unauthorized')}</AlertTitle>
            <AlertDescription className="me-2">
              {t('auth.youNeedToBeAnOwnerToManageApi')}{' '}
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <ApiTokenForm className="max-w-xl" tokens={tokens} />

          <hr className="mt-8 mb-4" />

          <h4 className="font-medium text-xl">{t('auth.yourExistingTokens')}</h4>

          {tokens && tokens.length === 0 && (
            <div className="mb-4">
              <p className="mt-2 text-muted-foreground text-sm italic">
                {t('auth.yourTokensWillBeShownHereOnceYouCreate')}{' '}
              </p>
            </div>
          )}

          {tokens && tokens.length > 0 && (
            <div className="mt-4 flex max-w-xl flex-col gap-y-4">
              {tokens.map((token) => (
                <div className="rounded-lg border border-border p-4" key={token.id}>
                  <div className="flex items-center justify-between gap-x-4">
                    <div>
                      <h5 className="text-base">{token.name}</h5>

                      <p className="mt-2 text-muted-foreground text-xs">
                        {t('auth.createdOn')} {formatDate(token.createdAt)}
                      </p>
                      {token.expiresAt ? (
                        <p className="mt-1 text-muted-foreground text-xs">
                          {t('auth.expiresOn')} {formatDate(token.expiresAt)}
                        </p>
                      ) : (
                        <p className="mt-1 text-muted-foreground text-xs">
                          {t('auth.tokenDoesnTHaveAnExpirationDate')}{' '}
                        </p>
                      )}
                    </div>

                    <div>
                      <TokenDeleteDialog token={token}>
                        <Button variant="destructive">{t('workspace.delete')}</Button>
                      </TokenDeleteDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
